import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  COLOR_REFERRAL_RULE_ROLE,
  LIST_REFERRAL_RULE_ROLE_OPTION,
} from "helpers/constans";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import DropdownCurrency from "components/Common/DropdownCurrency";
import DropdownAsyncTriggerEvent from "components/Common/DropdownAsyncTriggerEvent";
import { postReferralRule, putReferralRule } from "store/referral-rule/thunk";
import { NumericFormat } from 'react-number-format';
import { updateReward } from "store/referral-rewards/thunk";

export interface Props {
  id?: string;
  item?: any;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  lstLevel?: any;
  onCloseClick?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const RuleForm = ({
  id = "",
  item,
  triggerRefresh,
  isShowDetail = false,
  lstLevel,
  onCloseClick
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [triggerEventSelected, setTriggerEventSelected] = useState<any>('');
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const data = {
        from_role: values?.from_role,
        to_role: values?.to_role,
        level: values?.level,
        amount: values?.amount,
        currency: values?.currency,
        trigger_event_code: values?.trigger_event_code,
        status: values?.status == 1 ? 1 : 0
      };

      const response: any = id
        ? await putReferralRule(id, data)
        : await postReferralRule(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    from_role: Yup.string().required(`${t("From Role is required")}`),
    to_role: Yup.string().required(`${t("To Role is required")}`),
    level: Yup.string().required(`${t("Level is required")}`),
    amount: Yup.number().required(`${t("Amount is required")}`),
    currency: Yup.string().required(`${t("Currency is required")}`),
    trigger_event_code: Yup.string().required(`${t("Reward distribution settings is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      from_role: "",
      to_role: "",
      level: "",
      amount: null,
      currency: "KRW",
      trigger_event_code: "",
      status: 1
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleCloseToggle = () => {
    onCloseClick && onCloseClick();
  };

  useEffect(() => {
    if(item) {
      const triggerEvent = item?.trigger_event;
      if(triggerEvent?.code) {
        setTriggerEventSelected(
          {
            value: String(triggerEvent?.code),
            label: (
              <div className="d-flex align-items-center gap-2">
                <div>{`${triggerEvent?.name} ${triggerEvent?.valid_within_hours ? ` - ${t('Valid')} ` + triggerEvent?.valid_within_hours + ` ${t('hours')}`  : ''}`}</div>
              </div>
            ),
            data: triggerEvent,
          }
        )
      }

      formik.setValues({
        ...item,
        trigger_event_code: item?.trigger_event?.id
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Button Create Invitation Rule")} - ${t("Invitation Rules")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : handleCloseToggle}>
          {id ? t("Button Update Invitation Rule") : t("Button Create Invitation Rule")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 6) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              <form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col lg={4}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("From Role")}
                        <span className="text-danger"> *</span>
                      </Label>

                      <DropdownStatusNew
                        name="from_role"
                        dataList={LIST_REFERRAL_RULE_ROLE_OPTION || []}
                        placeholder={`${t("Select role")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.from_role}
                        onChangeSelect={(event: any) => {
                          formik.setFieldValue("from_role", event?.value);
                        }}
                        isHasOptionAll={false}
                        colors={COLOR_REFERRAL_RULE_ROLE}
                      />
                      {formik.touched.from_role && formik.errors.from_role ? (
                        <div className="text-danger mt-2">
                          {formik.errors.from_role}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={4}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("To Role")}
                        <span className="text-danger"> *</span>
                      </Label>

                      <DropdownStatusNew
                        name="to_role"
                        dataList={LIST_REFERRAL_RULE_ROLE_OPTION || []}
                        placeholder={`${t("Select role")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.to_role}
                        onChangeSelect={(event: any) => {
                          formik.setFieldValue("to_role", event?.value);
                        }}
                        isHasOptionAll={false}
                        colors={COLOR_REFERRAL_RULE_ROLE}
                      />
                      {formik.touched.to_role && formik.errors.to_role ? (
                        <div className="text-danger mt-2">
                          {formik.errors.to_role}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={4}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Level")}
                        <span className="text-danger"> *</span>
                      </Label>

                      <DropdownOptionNew
                        name="level"
                        dataList={lstLevel || []}
                        placeholder={`${t("Select level")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.level}
                        onChangeSelect={(event: any) => {
                          formik.setFieldValue("level", event?.value);
                        }}
                        isHasOptionAll={false}
                        hideSelectedOptions={false}
                      />
                      {formik.touched.level && formik.errors.level ? (
                        <div className="text-danger mt-2">
                          {formik.errors.level}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={4}>
                    <div className="mb-3">
                      <Label htmlFor="amount" className="form-label">
                        {t("Amount")} <span className="text-danger"> *</span>
                      </Label>

                      <NumericFormat 
                        className="form-control" 
                        placeholder={t("Amount")}
                        value={formik?.values?.amount} 
                        allowLeadingZeros 
                        thousandSeparator="," 
                        onValueChange={(values: any) => {
                          const {floatValue} = values;
                          formik.setFieldValue(
                            "amount",
                            floatValue
                          )
                        }
                        }
                      />
                      {formik.touched.amount && formik.errors.amount ? (
                        <div className="text-danger mt-2">
                          {formik.errors.amount}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xxl={4}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Currency")} <span className="text-danger"> *</span>
                      </Label>

                      <DropdownCurrency
                        name="currency"
                        placeholder={`${t("Select currency")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.currency}
                        onChangeSelect={(event: any) => {
                          formik.setFieldValue("currency", event?.value)
                        }}
                      />

                      {formik?.touched?.currency && formik?.errors?.currency ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.currency}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xxl={4}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("Reward Distribution Conditions")} <span className="text-danger"> *</span>
                      </Label>

                      <DropdownAsyncTriggerEvent
                        name="trigger_event_code"
                        placeholder={`${t("Select reward distribution")}...`}
                        listCheckedTrigger={triggerEventSelected}
                        onChangeSelect={(event: any) => {
                          setTriggerEventSelected(event)
                          formik.setFieldValue("trigger_event_code", event?.value)
                        }}
                      />

                      {formik?.touched?.trigger_event_code || formik?.errors?.trigger_event_code ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.trigger_event_code}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Status")}</Label>

                      <div>
                        <div
                          className="form-check form-switch form-switch-sm text-center"
                          dir="ltr"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="status"
                            checked={formik?.values?.status == 1 ? true : false}
                            onChange={(event) => {
                              formik.setFieldValue(
                                "status",
                                event?.target?.checked
                              );
                            }}
                          />
                        </div>
                      </div>

                      {formik?.touched?.status && formik?.errors?.status ? (
                        <div className="text-danger mt-2">
                          {/* {formik?.errors?.status} */}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Button Update")
                          : t("Button Create")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default RuleForm;
